import { trans } from '@spotahome/soyuz/client';
import { formatPrice } from '@spotahome/soyuz-currency';

import { getBestOffer } from '../utils/offers';
import { HOMECARD_IMAGE_SIZES } from '../constants';

import {
  hoursAfterAcceptanceKeys,
  formatSimpleCancellationDate
} from './cancellationPolicies';

export const getHomecardImageSet = photoUrls =>
  Object.keys(photoUrls)
    .filter(size => photoUrls[size] && HOMECARD_IMAGE_SIZES[size])
    .map(size => `${photoUrls[size]} ${HOMECARD_IMAGE_SIZES[size]}`)
    .join(', ');

export const getUrlWithMovingDates = (url, moveIn, moveOut) => {
  let getParams = [];
  getParams = moveIn ? [...getParams, `move-in=${moveIn}`] : getParams;
  getParams = moveOut ? [...getParams, `move-out=${moveOut}`] : getParams;

  return getParams.length ? `${url}?${getParams.join('&')}` : url;
};

export const getUrlWithMapParameters = (url, moveIn, moveOut, mapCenter) => {
  let getParams = [];
  getParams = moveIn ? [...getParams, `move-in=${moveIn}`] : getParams;
  getParams = moveOut ? [...getParams, `move-out=${moveOut}`] : getParams;
  getParams = mapCenter ? [...getParams, `mapCenter=${mapCenter}`] : getParams;

  const urlStartType = url.includes('?') ? '&' : '?';

  return getParams.length
    ? `${url}${urlStartType}map=true&${getParams.join('&')}`
    : `${url}${urlStartType}map=true`;
};

const areBillsIncluded = features => {
  // Required Bills to show label
  const requiredBills = {
    5000: 'electricity',
    5001: 'water',
    5002: 'gas',
    5003: 'wifi'
  };

  return Object.keys(requiredBills).every(billId => features[billId]);
};

export const getChips = (
  isNoSecurityDeposit,
  cancellationDeadline,
  features = {},
  showBillsIncludedLabel,
  offers,
  currencyIsoCode,
  isLongTerm,
  isBestChoice,
  isInstantBooking
) => {
  const chips = [];

  if (isBestChoice) {
    chips.push({
      label: trans('homecard.label.best-choice'),
      tooltip: trans('homecard.label.best-choice.tooltip')
    });
  }

  if (isNoSecurityDeposit) {
    chips.push({
      label: `${trans('homecard.label.no_deposit')}`,
      tooltip: `${trans('tooltip.message.no-deposit')}`
    });
  }

  if (isLongTerm) {
    chips.push({ label: trans('homecard.label.long-term') });
  }

  if (isInstantBooking) {
    chips.push({ label: trans('homecard.label.instant') });
  }

  if (showBillsIncludedLabel && areBillsIncluded(features)) {
    chips.push({
      label: trans('homecard.label.bills_included'),
      tooltip: trans('tooltip.message.bills_included')
    });
  }

  if (cancellationDeadline && cancellationDeadline.freeCancellationCurrently) {
    const hasHoursAfterAcceptance =
      cancellationDeadline.freeCancellationDeadlineType ===
      'hoursAfterAcceptance';

    const renderHoursAfterAcceptanceMessage = `${trans(
      `${hoursAfterAcceptanceKeys.title}`
    )} ${trans(`${hoursAfterAcceptanceKeys.afterBookingAccepted}`, {
      hour: cancellationDeadline.freeCancellationDeadlineValue
    })}`;

    chips.push({
      label: `${trans('homecard.label.free_cancellation')}`,
      tooltip: hasHoursAfterAcceptance
        ? renderHoursAfterAcceptanceMessage
        : `${trans(
            'cancellationPolicy.free.message',
            formatSimpleCancellationDate(
              cancellationDeadline.freeCancellationDeadlineValue
            )
          )}`
    });
  }

  const bestOffer = getBestOffer(offers);

  if (bestOffer) {
    let discount = `${bestOffer.discountValue} %`;
    if (bestOffer.discountType === 'fixed') {
      discount = formatPrice(bestOffer.discountValue, currencyIsoCode);
    }

    chips.push({
      label: trans('homecard.label.up_to_discount', { discount })
    });
  }

  return chips.map(chip => ({
    ...chip,
    label: chip.label.toUpperCase()
  }));
};

export const getRatingFromReviews = (reviews = {}) => reviews.ratingAverage;
